import { DollarSign, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "@/lib/formatters";
import { budgetService } from "@/lib/services/admin";
import { teamService } from "@/lib/services/teams";
import { userService } from "@/lib/services/users";
import type { BudgetConfig, Team, User } from "@/types";

// ── Helpers (pure functions, no hooks) ───────────────────────────────────────

function scopeLabel(config: BudgetConfig): string {
  if (config.scope === "platform") return "Default Per-User Budget";
  if (config.scope === "team") return config.teamName || `Team #${config.teamId}`;
  if (config.scope === "user") return config.userName || config.userEmail || `User #${config.userId}`;
  return config.scope;
}

function scopeDescription(config: BudgetConfig): string | null {
  if (config.scope === "platform") return "Applies to every user unless overridden by a team or user budget";
  if (config.scope === "team") return "Applies to all members of this team";
  return null;
}

function scopeBadgeClass(scope: string): string {
  if (scope === "platform") return "bg-blue-500/10 text-blue-400 border-blue-500/20";
  if (scope === "team") return "bg-purple-500/10 text-purple-400 border-purple-500/20";
  return "bg-green-500/10 text-green-400 border-green-500/20";
}

// ── Config row component ─────────────────────────────────────────────────────

interface BudgetConfigRowProps {
  config: BudgetConfig;
  editingId: number | null;
  formBudgetValue: string;
  onFormChange: (value: string) => void;
  onStartEdit: (config: BudgetConfig) => void;
  onCancelEdit: () => void;
  onUpdate: (id: number) => void;
  onDelete: (id: number) => void;
}

function BudgetConfigRow({
  config,
  editingId,
  formBudgetValue,
  onFormChange,
  onStartEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
}: Readonly<BudgetConfigRowProps>) {
  const isEditing = editingId === config.id;
  const desc = scopeDescription(config);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`text-xs px-2 py-0.5 rounded-full border ${scopeBadgeClass(config.scope)} capitalize shrink-0`}
        >
          {config.scope}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-content-primary">{scopeLabel(config)}</span>
            {config.scope === "user" && config.userEmail && (
              <span className="text-xs text-content-quaternary">{config.userEmail}</span>
            )}
          </div>
          {desc && <p className="text-xs text-content-quaternary mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={formBudgetValue}
              onChange={(e) => onFormChange(e.target.value)}
              className="w-28 px-2 py-1 bg-surface-primary border border-border-primary rounded text-sm text-content-primary focus:outline-hidden"
            />
            <button
              type="button"
              onClick={() => onUpdate(config.id)}
              className="text-xs text-brand-primary hover:underline"
            >
              Save
            </button>
            <button type="button" onClick={onCancelEdit} className="text-xs text-content-tertiary hover:underline">
              Cancel
            </button>
          </div>
        ) : (
          <>
            <span className="text-sm font-medium text-content-primary">
              {formatCurrency(config.monthlyBudgetUsd)}
              <span className="text-content-tertiary font-normal">/{config.scope === "user" ? "mo" : "user/mo"}</span>
            </span>
            <button
              type="button"
              onClick={() => onStartEdit(config)}
              className="p-1 text-content-tertiary hover:text-content-primary transition"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(config.id)}
              className="p-1 text-content-tertiary hover:text-red-400 transition"
              title="Remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Create form component ────────────────────────────────────────────────────

interface BudgetCreateFormProps {
  scope: string;
  monthlyBudgetUsd: string;
  selectedTeamUuid: string;
  selectedUserUuid: string;
  teams: Team[];
  users: User[];
  onScopeChange: (scope: string) => void;
  onBudgetChange: (value: string) => void;
  onTeamChange: (uuid: string) => void;
  onUserChange: (uuid: string) => void;
  onCreate: () => void;
  onCancel: () => void;
}

function BudgetCreateForm({
  scope,
  monthlyBudgetUsd,
  selectedTeamUuid,
  selectedUserUuid,
  teams,
  users,
  onScopeChange,
  onBudgetChange,
  onTeamChange,
  onUserChange,
  onCreate,
  onCancel,
}: Readonly<BudgetCreateFormProps>) {
  return (
    <div className="bg-surface-secondary border border-border-secondary rounded-lg p-4 mb-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <label htmlFor="budget-scope" className="block text-xs text-content-secondary mb-1">
            Scope
          </label>
          <select
            id="budget-scope"
            value={scope}
            onChange={(e) => onScopeChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary focus:outline-hidden"
          >
            <option value="platform">Platform Default</option>
            <option value="team">Team</option>
            <option value="user">User</option>
          </select>
        </div>

        {scope === "team" && (
          <div className="flex-1 min-w-[180px]">
            <label htmlFor="budget-team" className="block text-xs text-content-secondary mb-1">
              Team
            </label>
            <select
              id="budget-team"
              value={selectedTeamUuid}
              onChange={(e) => onTeamChange(e.target.value)}
              className="w-full px-3 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary focus:outline-hidden"
            >
              <option value="">Select a team...</option>
              {teams.map((t) => (
                <option key={t.uuid} value={t.uuid}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {scope === "user" && (
          <div className="flex-1 min-w-[180px]">
            <label htmlFor="budget-user" className="block text-xs text-content-secondary mb-1">
              User
            </label>
            <select
              id="budget-user"
              value={selectedUserUuid}
              onChange={(e) => onUserChange(e.target.value)}
              className="w-full px-3 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary focus:outline-hidden"
            >
              <option value="">Select a user...</option>
              {users.map((u) => (
                <option key={u.uuid} value={u.uuid}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-1 min-w-[140px]">
          <label htmlFor="budget-monthly-usd" className="block text-xs text-content-secondary mb-1">
            Monthly Budget (USD)
          </label>
          <input
            id="budget-monthly-usd"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="100.00"
            value={monthlyBudgetUsd}
            onChange={(e) => onBudgetChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary focus:outline-hidden"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white text-sm rounded-lg transition"
          >
            Create
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-surface-primary border border-border-primary text-sm text-content-secondary rounded-lg hover:bg-surface-secondary transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function AdminBudgets() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { refreshProfile } = useAuth();
  const [configs, setConfigs] = useState<BudgetConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ scope: "platform" as string, monthlyBudgetUsd: "" });
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string>("");
  const [selectedUserUuid, setSelectedUserUuid] = useState<string>("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const res = await budgetService.list();
      setConfigs(res.data);
    } catch {
      showToast("Failed to load budget configs", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadTeamsAndUsers = async () => {
    try {
      const [teamsRes, usersRes] = await Promise.all([teamService.list(), userService.list({ pageSize: 100 })]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data);
    } catch {
      // Non-fatal — dropdowns will be empty
    }
  };

  const handleCreate = async () => {
    const amount = Number.parseFloat(formData.monthlyBudgetUsd);
    if (!amount || amount <= 0) {
      showToast("Please enter a valid budget amount", "warning");
      return;
    }
    if (formData.scope === "team" && !selectedTeamUuid) {
      showToast("Please select a team", "warning");
      return;
    }
    if (formData.scope === "user" && !selectedUserUuid) {
      showToast("Please select a user", "warning");
      return;
    }
    try {
      await budgetService.create({
        scope: formData.scope,
        teamUuid: formData.scope === "team" ? selectedTeamUuid || undefined : undefined,
        userUuid: formData.scope === "user" ? selectedUserUuid || undefined : undefined,
        monthlyBudgetUsd: amount,
      });
      showToast("Budget created", "success");
      setShowForm(false);
      setFormData({ scope: "platform", monthlyBudgetUsd: "" });
      setSelectedTeamUuid("");
      setSelectedUserUuid("");
      await loadConfigs();
      refreshProfile().catch(() => {});
    } catch {
      showToast("Failed to create budget", "error");
    }
  };

  const handleUpdate = async (id: number) => {
    const amount = Number.parseFloat(formData.monthlyBudgetUsd);
    if (!amount || amount <= 0) {
      showToast("Please enter a valid budget amount", "warning");
      return;
    }
    try {
      await budgetService.update(id, { monthlyBudgetUsd: amount });
      showToast("Budget updated", "success");
      setEditingId(null);
      setFormData({ scope: "platform", monthlyBudgetUsd: "" });
      await loadConfigs();
      refreshProfile().catch(() => {});
    } catch {
      showToast("Failed to update budget", "error");
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: "Remove Budget Limit",
      message:
        "Are you sure you want to remove this budget limit? Users affected by this budget will become unlimited.",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await budgetService.delete(id);
      showToast("Budget removed", "success");
      await loadConfigs();
      refreshProfile().catch(() => {});
    } catch {
      showToast("Failed to delete budget", "error");
    }
  };

  const startEdit = (config: BudgetConfig) => {
    setEditingId(config.id);
    setFormData({ scope: config.scope, monthlyBudgetUsd: String(config.monthlyBudgetUsd) });
    setShowForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ scope: "platform", monthlyBudgetUsd: "" });
  };

  if (loading && configs.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium text-content-primary flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Budget Limits
          </h3>
          <p className="text-xs text-content-tertiary mt-0.5">
            Set monthly AI spending limits per user, team, or platform-wide.
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setFormData({ scope: "platform", monthlyBudgetUsd: "" });
              setSelectedTeamUuid("");
              setSelectedUserUuid("");
              loadTeamsAndUsers();
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white text-sm rounded-lg transition"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Budget
          </button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <BudgetCreateForm
          scope={formData.scope}
          monthlyBudgetUsd={formData.monthlyBudgetUsd}
          selectedTeamUuid={selectedTeamUuid}
          selectedUserUuid={selectedUserUuid}
          teams={teams}
          users={users}
          onScopeChange={(scope) => {
            setFormData((f) => ({ ...f, scope }));
            setSelectedTeamUuid("");
            setSelectedUserUuid("");
          }}
          onBudgetChange={(value) => setFormData((f) => ({ ...f, monthlyBudgetUsd: value }))}
          onTeamChange={setSelectedTeamUuid}
          onUserChange={setSelectedUserUuid}
          onCreate={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Configs list */}
      {configs.length === 0 ? (
        <p className="text-center text-content-tertiary py-8 text-sm">
          No budget limits configured. AI usage is unlimited.
        </p>
      ) : (
        <div className="space-y-2">
          {configs.map((config) => (
            <BudgetConfigRow
              key={config.id}
              config={config}
              editingId={editingId}
              formBudgetValue={formData.monthlyBudgetUsd}
              onFormChange={(v) => setFormData((f) => ({ ...f, monthlyBudgetUsd: v }))}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
